import { useId, useState, type ReactNode } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
  ExclamationTriangleIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { ModalShell } from '../../ui/ModalShell';
import { api } from '../../../services/api';
import { copyPromisedText, copyToClipboard } from '../../../lib/clipboard';
import type { DiagnosticReport, DiagnosticShareLink, ReportBody } from '../../../types/diagnostics';
import {
  confidenceLabel,
  confidenceTone,
  formatCost,
  isReportInFlight,
  readLastSymptom,
  whatsappShareUrl,
} from './reportLogic';
import { ReportChip, Spinner } from './ReportChip';
import { SafeMarkdown } from './SafeMarkdown';
import { SymptomForm } from './SymptomForm';
import { useDiagnosticReport, useStartDiagnosis } from './useDiagnosticReport';

/**
 * A Diagnose report, opened from a bundle row or straight from the
 * notification email (`?report=drp_…`). Built for a phone first: the thing
 * the admin needs to do next — message the skipper — comes straight after the
 * headline, then the fix link for a Claude Code session, then the detail.
 */
export function DiagnosticReportSheet({
  deviceId,
  reportId,
  onClose,
  onOpenReport,
}: {
  deviceId: string;
  reportId: string | null;
  onClose: () => void;
  /** Switch the sheet to another report (a retry or a re-run). */
  onOpenReport: (reportId: string) => void;
}) {
  return (
    <ModalShell open={!!reportId} onClose={onClose} title="Diagnose report" size="2xl" keyboardAlign="above">
      {reportId && <ReportContent key={reportId} deviceId={deviceId} reportId={reportId} onOpenReport={onOpenReport} />}
    </ModalShell>
  );
}

function ReportContent({
  deviceId,
  reportId,
  onOpenReport,
}: {
  deviceId: string;
  reportId: string;
  onOpenReport: (reportId: string) => void;
}) {
  const { data: report, isLoading, isError } = useDiagnosticReport(deviceId, reportId);
  const start = useStartDiagnosis(deviceId);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const diagnose = (symptom: string | undefined) => {
    if (!report) return;
    setStartError(null);
    start.mutate(
      { bundleId: report.bundle_id, symptom },
      {
        onSuccess: (result) => {
          if ('reportId' in result) onOpenReport(result.reportId);
          else setStartError(result.error.message);
        },
        onError: () => setStartError('Could not start the diagnosis. Please try again.'),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 py-8 text-[15px] text-slate-400">
        <Spinner /> Loading report…
      </div>
    );
  }
  if (isError || !report) {
    return <p className="py-8 text-[15px] text-red-400">Could not load this report. Check your connection and try again.</p>;
  }

  const rerun = (
    <div className="space-y-3">
      {startError && <p role="alert" className="text-[15px] text-amber-300">{startError}</p>}
      {rerunOpen ? (
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
          <SymptomForm
            initialValue={report.symptom ?? readLastSymptom(deviceId)}
            submitLabel="Start diagnosis"
            pendingLabel="Starting…"
            pending={start.isPending}
            onSubmit={diagnose}
            onCancel={() => setRerunOpen(false)}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setRerunOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-700 px-4 py-3 text-[15px] text-slate-200 transition-colors hover:bg-slate-600"
        >
          <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
          Re-run with a different symptom
        </button>
      )}
    </div>
  );

  return (
    <div className="min-w-0 space-y-5">
      {report.symptom && report.status !== 'ready' && <SymptomQuote symptom={report.symptom} />}

      {isReportInFlight(report.status) && (
        <div className="flex items-start gap-3 rounded-lg border border-cyan-800/60 bg-cyan-950/30 p-4">
          <Spinner className="mt-0.5 h-5 w-5 text-cyan-300" />
          <div>
            <p className="text-[16px] font-medium text-cyan-100">Claude is reading the bundle — usually under a minute.</p>
            <p className="mt-1 text-[14px] text-slate-400">You can close this — the result appears on the bundle when it is done.</p>
          </div>
        </div>
      )}

      {report.status === 'failed' && (
        <div className="space-y-3 rounded-lg border border-red-800/60 bg-red-950/30 p-4">
          <p className="flex items-start gap-2 text-[16px] font-medium text-red-200">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            The diagnosis failed.
          </p>
          {report.error && <p className="break-words text-[15px] text-red-200/90">{report.error}</p>}
          <button
            type="button"
            onClick={() => diagnose(report.symptom ?? undefined)}
            disabled={start.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-700 px-4 py-3 text-[15px] text-white transition-colors hover:bg-slate-600 disabled:opacity-50"
          >
            <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
            {start.isPending ? 'Starting…' : 'Retry'}
          </button>
        </div>
      )}

      {report.status === 'ready' && report.report && (
        <ReadyReport deviceId={deviceId} report={report} body={report.report} />
      )}

      {!isReportInFlight(report.status) && rerun}
    </div>
  );
}

function SymptomQuote({ symptom }: { symptom: string }) {
  return (
    <div className="rounded-lg bg-slate-900/40 px-4 py-3">
      <p className="text-[13px] uppercase tracking-wider text-slate-500">Skipper reported</p>
      <p className="mt-1 whitespace-pre-wrap break-words text-[15px] text-slate-300">{symptom}</p>
    </div>
  );
}

function Section({
  title,
  children,
  className = '',
  highlight,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  highlight?: boolean;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      data-highlight={highlight === undefined ? undefined : String(highlight)}
      className={`min-w-0 rounded-lg border p-4 ${className || 'border-slate-700 bg-slate-900/40'}`}
    >
      <h4 id={id} className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-slate-400">{title}</h4>
      {children}
    </section>
  );
}

const ACTION_BUTTON =
  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-[15px] font-medium transition-colors';

function ReadyReport({ deviceId, report, body }: { deviceId: string; report: DiagnosticReport; body: ReportBody }) {
  const fleet = body.fleet_impact;
  const fleetHighlight = !!fleet && fleet.matching_devices > 1;

  return (
    <>
      <div className="space-y-2">
        <h3 className="break-words text-[19px] font-semibold leading-snug text-white">{body.headline}</h3>
        <ReportChip chip={{ label: confidenceLabel(body.confidence), tone: confidenceTone(body.confidence), spinner: false }} />
      </div>

      <SkipperMessage message={body.skipper_workaround.message} available={body.skipper_workaround.available} />
      <FixLink deviceId={deviceId} reportId={report.id} />

      <Section title="Diagnosis">
        <SafeMarkdown source={body.diagnosis} />
      </Section>

      {body.evidence.length > 0 && (
        <Section title="Evidence">
          <ul className="space-y-4">
            {body.evidence.map((e, i) => (
              <li key={i} className="min-w-0 space-y-1.5">
                <p className="text-[13px] text-slate-500">
                  <span className="font-mono">{e.source}</span>
                  {e.timestamp ? ` · ${e.timestamp}` : ''}
                </p>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-950/70 p-3 font-mono text-[13px] leading-relaxed text-slate-200">{e.excerpt}</pre>
                <p className="break-words text-[15px] text-slate-300">{e.note}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Suspected cause">
        <dl className="space-y-3 text-[15px]">
          <div>
            <dt className="text-[13px] text-slate-500">Component</dt>
            <dd className="break-words text-slate-200">{body.suspected_cause.component}</dd>
          </div>
          {body.suspected_cause.introduced_in && (
            <div>
              <dt className="text-[13px] text-slate-500">Introduced in</dt>
              <dd className="break-words text-slate-200">{body.suspected_cause.introduced_in}</dd>
            </div>
          )}
        </dl>
        <p className="mt-3 break-words text-[15px] text-slate-300">{body.suspected_cause.explanation}</p>
        {body.suspected_cause.files.length > 0 && (
          <div className="mt-3">
            <p className="text-[13px] text-slate-500">Likely files — a best guess, Claude did not see the source</p>
            <ul className="mt-1 space-y-1">
              {body.suspected_cause.files.map((f) => (
                <li key={f} className="break-all font-mono text-[13px] text-slate-300">{f}</li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {body.comparison && (
        <Section title="Compared with the previous bundle">
          <p className="break-words text-[15px] text-slate-300">{body.comparison}</p>
        </Section>
      )}

      {fleet && (
        <Section
          title="Fleet impact"
          highlight={fleetHighlight}
          className={fleetHighlight ? 'border-amber-600/70 bg-amber-950/30' : ''}
        >
          <p className={`text-[15px] ${fleetHighlight ? 'font-medium text-amber-200' : 'text-slate-300'}`}>
            {`${fleet.matching_devices} of ${fleet.same_version_devices} Scouts on the same version show the same failure.`}
          </p>
          {fleet.recommendation && (
            <p className={`mt-2 break-words text-[15px] ${fleetHighlight ? 'text-amber-100' : 'text-slate-400'}`}>{fleet.recommendation}</p>
          )}
        </Section>
      )}

      {body.bundle_gaps.length > 0 && (
        <Section title="What the bundle lacked">
          <ul className="list-disc space-y-1 pl-5 text-[15px] text-slate-300">
            {body.bundle_gaps.map((g) => <li key={g} className="break-words">{g}</li>)}
          </ul>
        </Section>
      )}

      {report.symptom && <SymptomQuote symptom={report.symptom} />}

      <p className="break-words text-[13px] text-slate-500">
        {[
          report.model ?? 'Model unknown',
          formatCost(report.cost_cents),
          report.completed_at ? `generated ${formatDistanceToNow(new Date(report.completed_at), { addSuffix: true })}` : null,
        ].filter(Boolean).join(' · ')}
      </p>
    </>
  );
}

function SkipperMessage({ message, available }: { message: string; available: boolean }) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    if (await copyToClipboard(message)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    }
  };

  const share = () => {
    // A dismissed share sheet rejects; that is not an error worth showing.
    navigator.share({ text: message }).catch(() => undefined);
  };

  return (
    <Section title="Message for the skipper" className="border-cyan-600/70 bg-cyan-950/30">
      {!available && (
        <p className="mb-3 flex items-start gap-2 text-[14px] text-amber-300">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          There is no safe workaround yet — this message tells the skipper what to expect.
        </p>
      )}
      <p className="whitespace-pre-wrap break-words text-[16px] leading-relaxed text-white">{message}</p>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
        <a
          href={whatsappShareUrl(message)}
          target="_blank"
          rel="noopener noreferrer"
          className={`${ACTION_BUTTON} bg-green-600 text-white hover:bg-green-500`}
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden="true" />
          Send on WhatsApp
        </a>
        <button type="button" onClick={copy} className={`${ACTION_BUTTON} bg-slate-700 text-white hover:bg-slate-600`}>
          <ClipboardDocumentIcon className="h-5 w-5" aria-hidden="true" />
          {copied ? 'Copied' : 'Copy'}
        </button>
        {canShare && (
          <button type="button" onClick={share} className={`${ACTION_BUTTON} bg-slate-700 text-white hover:bg-slate-600`}>
            <ShareIcon className="h-5 w-5" aria-hidden="true" />
            Share
          </button>
        )}
      </div>
    </Section>
  );
}

type ShareState = 'idle' | 'working' | 'copied' | 'shown' | 'error';

function FixLink({ deviceId, reportId }: { deviceId: string; reportId: string }) {
  const [link, setLink] = useState<DiagnosticShareLink | null>(null);
  const [state, setState] = useState<ShareState>('idle');

  const copyLink = async () => {
    if (link) {
      setState((await copyToClipboard(link.url)) ? 'copied' : 'shown');
      return;
    }
    setState('working');
    const request = api.adminShareDiagnosticReport(deviceId, reportId);
    // Start the clipboard write inside the tap; see copyPromisedText.
    const copied = copyPromisedText(request.then((l) => l.url));
    try {
      const created = await request;
      setLink(created);
      setState((await copied) ? 'copied' : 'shown');
    } catch {
      setState('error');
    }
  };

  const expiry = link ? format(new Date(link.expires_at), 'd MMM yyyy, HH:mm') : null;

  return (
    <Section title="Fix link">
      <p className="mb-3 text-[15px] text-slate-300">
        Paste into a Claude Code session to have the permanent fix written. The link carries the report and the bundle — no login needed.
      </p>
      <button
        type="button"
        onClick={copyLink}
        disabled={state === 'working'}
        className={`${ACTION_BUTTON} w-full bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50 sm:w-auto`}
      >
        <CodeBracketIcon className="h-5 w-5" aria-hidden="true" />
        {state === 'working' ? 'Creating link…' : 'Copy link for Claude Code'}
      </button>
      {state === 'error' && <p role="alert" className="mt-3 text-[15px] text-red-400">Could not create the link. Please try again.</p>}
      {link && (
        <div className="mt-3 space-y-2">
          <input
            readOnly
            value={link.url}
            aria-label="Fix link address"
            onFocus={(e) => e.currentTarget.select()}
            className="block w-full min-w-0 rounded-lg border border-slate-600 bg-slate-950/70 px-3 py-2 font-mono text-[14px] text-slate-200"
          />
          <p className="text-[14px] text-slate-400">
            {state === 'copied' ? 'Copied — ' : 'Could not copy automatically; select the link above and copy it. It is '}
            valid for 7 days (until {expiry}).
          </p>
        </div>
      )}
    </Section>
  );
}

export default DiagnosticReportSheet;
